import { DataNode } from 'components/WorkspaceEditor/types'
import { useEffect, useMemo, useRef } from 'react'
import { useHandleCardPositions } from './useHandleCardPositions'
import { useTrackChangeCards } from './useTrackChangeCards'

/**
 * Runs handleCardPositions only on initial render if conditions are met.
 * @description This hook is used to set initial card positions when the component mounts.
 * It checks if the treeData is not empty and if the current suggestion card filter is the default one.
 * If both conditions are true, it calls handleCardPositions to set the initial positions of the suggestion cards.
 * It uses a ref to ensure that the effect runs only on the initial render.
 * This is useful for initializing the state of the suggestion cards in the UI.
 * @param treeData - The tree nodes to check.
 * @returns {void}
 */
export const useInitialCardPositions = (treeData: DataNode[]) => {
    const handleCardPositions = useHandleCardPositions()
    const initialRender = useRef(true)
    const { isNotDefaultSuggestionCardFilter } = useTrackChangeCards()

    const treeDataState = useMemo(
        () => ({
            hasData: treeData.length > 0,
            length: treeData.length,
        }),
        [treeData.length]
    )

    const shouldRun = useMemo(
        () => treeDataState.hasData && !isNotDefaultSuggestionCardFilter,
        [treeDataState.hasData, isNotDefaultSuggestionCardFilter]
    )

    useEffect(() => {
        if (!initialRender.current) return

        if (shouldRun) {
            handleCardPositions()
            initialRender.current = false
        }
    }, [shouldRun, handleCardPositions])
}
